import {course_getTeachers_Api, exam_deleteExaminationPaper_Api, exam_getExaminationPapers_Api,} from 'apis'
import {ROOT_URL} from 'envs/_current/config'
import getLinkToDetail from 'helpers/getLinkToDetail'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useMemo, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {branch, compose} from 'recompose'
import {ContentNotFound} from 'views/AppLocals/ContentNotFound'
import {showDeleteConfirmDialog} from 'views/AppLocals/factory/createConfirmDialog'
import createContextMenu from 'views/AppLocals/factory/createContextMenu'
import {ExaminationPaperSortBy} from 'views/AppLocals/factory/createEntitySortBy'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {NotificationActionTypes, successNotify,} from 'views/AppLocals/factory/createNotification'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import usePaginationHelper from 'views/AppLocals/hooks/usePaginationHelper'
import {NullAsDefault} from 'views/Shared'
import CRUD from 'views/Shared/enums/CRUD'
import {ExaminationPaperFilter} from './ExaminationsFilter'
import {renderExaminationPaperItem} from './renderExaminationPaperItem'
import {useRouteSearchParam} from "../../../envs/_current/history";
import {RiSearchEyeLine} from "react-icons/ri";
import {BiTrash} from "react-icons/bi";
import PagePaginationWrapper from "../../../modules/asyncCache/components/PagePagination/PagePaginationWrapper";
import {renderTeacher} from "../Course/functions/renderTeacher";
import {renderLoading} from "../../AppLocals/functions/renderers/renderLoading";
import EmptyHolder from "../../../components/EmptyHolder";
import {LayoutContext} from "../../../components/layouts/Default/LayoutContext";

const usePaperList = ({owner_id, owner_type}) => {
    const t = useTranslate()

    const [refreshKey, setRefreshKey] = useState()

    const [action, setAction] = useState({
        type: undefined,
        value: undefined,
    })

    const reload = () => {
        setRefreshKey(Date.now())
    }

    const onError = notifyOnError(t)

    const {handleAsyncAction: onDelete} = useAsyncAction({
        apiInfo: exam_deleteExaminationPaper_Api,
        onError,
        onSuccess: () => {
            successNotify(NotificationActionTypes.DELETE, t)
            reload()
        },
    })

    const handleAction = (action, params) => {
        const {id} = params || {}
        switch (action) {
            case CRUD.READ:
                if (id) {
                    const pathname = ROOT_URL + getLinkToDetail(params)
                    window.open(pathname, '_blank', 'noopener,noreferrer')
                }
                break
            case CRUD.DELETE:
                if (id) {
                    showDeleteConfirmDialog({
                        translate: t,
                        title: 'delete examination paper',
                        message: 'delete examination paper content',
                        onOk: () => {
                            onDelete({}, {id})
                        },
                    })
                }
                break
            default:
                break
        }
    }

    const onCancel = () => {
        setAction({})
    }

    const renderContextMenu = useCallback(
        (item) =>
            createContextMenu(
                [
                    {
                        icon: <RiSearchEyeLine size={14} className="text-green-600"/>,
                        label: 'view',
                        key: CRUD.READ,
                    },
                    {
                        icon: <BiTrash size={14} className="text-primary-300"/>,
                        label: 'delete',
                        key: CRUD.DELETE,
                    },
                ].filter((e) => item),
                (key) => {
                    handleAction(key, item)
                }
            ),
        []
    )

    const modal = useMemo(() => {
        switch (action.type) {
            default:
                break
        }
        return null
    }, [action, owner_id, owner_type])

    return {
        modal,
        action,
        onCancel,
        refreshKey,
        renderContextMenu,
    }
}

const ExaminationPaperLists = ({
                                   owner_id,
                                   owner_type,
                                   Wrapper = 'div',
                                   filter_actions = {
                                       sortBy: 'sort-by',
                                       keyword: 'keyword',
                                       result_status: 'status',
                                       examination_id: 'examination',
                                   },
                               }) => {
    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)

    const location = useLocation()

    const sortBy = useRouteSearchParam(filter_actions.sortBy)

    const result_status = useRouteSearchParam(filter_actions.result_status)

    const examination_id = useRouteSearchParam(filter_actions.examination_id)

    const {refreshKey, renderContextMenu} = usePaperList({
        owner_id,
        owner_type,
    })

    const refresh_deps = useMemo(
        () => [
            owner_id,
            owner_type,
            refreshKey,
            `refreshToken=${_.get(location.state, 'refreshToken')}`,
        ],
        [owner_id, owner_type, refreshKey, location.state]
    )

    const renderList = useCallback(
        ({keyword, sortBy: currentSort, filterBy, refreshKey}) => (
            <PagePaginationWrapper
                apiInfo={exam_getExaminationPapers_Api}
                query={bindQueryParams([{id: owner_id}, {type: owner_type}])}
                values={{...filter_actions, keyword, sort_by: currentSort, ...(filterBy ?? {})}}
                refreshKey={refreshKey}
                renderItem={renderExaminationPaperItem({
                    translate: t,
                    renderContextMenu,
                })}
                renderLoading={() => renderLoading()}
                renderEmpty={() => <EmptyHolder/>}
                Wrapper={({children}) => (
                    <div className="mt-2 flex flex-col divide-y divide-gray-200 list-item-workspace">
                        {children}
                    </div>
                )}
            />
        ),
        [owner_id, owner_type, t, renderContextMenu, isLg, filter_actions]
    )

    const [pager_widget] = usePaginationHelper({
        onInit: ({onSortChange = NullAsDefault, onFilterChange = NullAsDefault}) => {
            onSortChange(sortBy)
            onFilterChange({
                result_status,
                examination_id,
            })
        },
        renderList,
        // pagerInfo: {
        //     RenderEntity: React.Fragment,
        //     PaginationMeta: NullAsDefault,
        //     apiInfo: exam_getExaminationPapers_Api,
        //     query: bindQueryParams([{id: owner_id}, {type: owner_type}]),
        //     renderItem: renderExaminationPaperItem({
        //         translate: t,
        //         renderContextMenu,
        //     }),
        //     renderWrap: (child) => (
        //         <div className="mt-2 flex flex-col divide-y divide-gray-200 list-item-workspace">
        //             {child}
        //         </div>
        //     ),
        // },
        filter_params: {
            owner_id,
            owner_type,
            result_status,
            examination_id,
        },
        query_params: filter_actions,
        sortHeader: t('list of examination records'),
        SortByWidget: ExaminationPaperSortBy,
        AdvancedFilter: ExaminationPaperFilter,
        dependencies: [owner_id, owner_type, examination_id],
        withKey: ({sortBy, keyword, filterBy}) =>
            [...refresh_deps, sortBy, keyword, JSON.stringify(filterBy)].join('/'),
    })
    return (
        <Wrapper className="flex flex-col gap-3">
            <div className="flex flex-col flex-1">
                <h2 className="font-bold text-xl text-gray-800 tracking-wide">
                    {t('examination records')}
                </h2>
                <div className="mt-1 w-20 h-1 rounded-full bg-gray-400"/>
            </div>
            {pager_widget}
            <div className="h-16"/>
        </Wrapper>
    )
}

export default compose(
    branch(
        ({owner_id, owner_type}) => _.some([owner_id, owner_type], _.isEmpty),
        () => () => <ContentNotFound/>
    )
)(ExaminationPaperLists)
